import { useEffect, useState } from "react";
import { Stack, Typography } from "@material-ui/core";
import firebase from "firebase/compat/app";
import { useTranslation } from "react-i18next";
import { NewMultipleContactPicker } from "./NewContactPicker";
import {
  getFavoritesListingsQuery,
  getContactsThatHaveListingInFavoritesQuery,
  createFavoriteIfNotExistsAndAddListingToFavorites
} from "utils/Haim/firestoreUtils";
import useAuth from "hooks/useAuth";
import { BuyerMatchedBulkMessage } from "./BuyerMatchedBulkMessage";
import { BuyerMatchedToListingContacts } from "./BuyerMatchedToListingContacts";
import {
  audienceKey,
  matchAudienceForListing
} from "../listingMatchToContacts/matchAudience";
import type {
  RealEstateContact,
  ProcessedListingData,
  FavoriteListingsOfContact
} from "../../../@types/sharedSchema";
import type { AlgoliaHit } from "instantsearch.js";

type BuyersMatchedToListingDialogProps = {
  customListingId: string | null;
  listing: AlgoliaHit<ProcessedListingData>;
};

export function BuyersMatchedToListing({
  listing,
  customListingId
}: BuyersMatchedToListingDialogProps): JSX.Element {
  const { user } = useAuth();

  const { t: translate } = useTranslation();

  const [favoriteListingsOfContacts, setFavoriteListingsOfContacts] = useState<
    FavoriteListingsOfContact[]
  >([]);

  const [selectedContacts, setSelectedContacts] = useState<RealEstateContact[]>([]);

  const [loading, setLoading] = useState(true);

  const listingId = listing?.objectID;

  useEffect(() => {
    if (!listingId) return;

    setLoading(true);

    const favoriteContactsSnapshot = getContactsThatHaveListingInFavoritesQuery(
      user.email,
      listingId
    ).onSnapshot(
      (snapshot) => {
        const data = snapshot.docs.map((doc) => doc.data());

        const parsedSelectedContacts = parseContactsFromFavoriteDocs(snapshot.docs);

        setFavoriteListingsOfContacts(data);
        setSelectedContacts(parsedSelectedContacts);

        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );

    return favoriteContactsSnapshot;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  const handleSubmit = async (newSelectedContacts: RealEstateContact[]): Promise<void> => {
    setLoading(true);

    try {
      await manageBuyersMatchedToListing({
        listingId,
        userEmail: user.email,
        selectedContacts: newSelectedContacts
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack gap={2}>
      <Typography variant="h6">
        {translate(audienceKey(matchAudienceForListing(listing), "buyersMatchedToListing"))}
      </Typography>
      <NewMultipleContactPicker
        loading={loading}
        selectedContacts={selectedContacts}
        onChangeAction={handleSubmit}
        setSelectedContacts={setSelectedContacts}
      />
      {!customListingId && (
        <BuyerMatchedToListingContacts
          loading={loading}
          listingId={listingId}
          selectedContacts={selectedContacts}
          favoriteListingsOfContacts={favoriteListingsOfContacts}
        />
      )}
      <BuyerMatchedBulkMessage
        listing={listing}
        loading={loading}
        selectedContacts={selectedContacts}
      />
    </Stack>
  );
}

function parseContactsFromFavoriteDocs(
  docs: firebase.firestore.QueryDocumentSnapshot<FavoriteListingsOfContact>[]
): RealEstateContact[] {
  return docs
    .map((doc) => {
      const { contactId } = doc.data();

      // The id IS the phone; nothing else is needed to identify the contact.
      return { id: contactId };
    })
    .filter(({ id }) => !!id) as RealEstateContact[];
}

export async function getContactsFavoriteFromListing(
  userEmail: string,
  listingId: string
): Promise<RealEstateContact[]> {
  const favoriteContactsSnapshot = await getContactsThatHaveListingInFavoritesQuery(
    userEmail,
    listingId
  ).get();

  return parseContactsFromFavoriteDocs(favoriteContactsSnapshot.docs);
}

type ManageBuyersMatchedToListingProps = {
  listingId: string;
  userEmail: string;
  selectedContacts: RealEstateContact[];
};

type ManageBuyersMatchedToListingReturn = {
  contactsToAddListing: RealEstateContact[];
  contactsToDeleteListing: RealEstateContact[];
};

export async function manageBuyersMatchedToListing({
  listingId,
  userEmail,
  selectedContacts
}: ManageBuyersMatchedToListingProps): Promise<ManageBuyersMatchedToListingReturn> {
  const currentListingFavoriteContacts = await getContactsFavoriteFromListing(userEmail, listingId);

  const contactsToDeleteListing = currentListingFavoriteContacts.filter(
    (newContact) => !selectedContacts.some((contact) => contact.id === newContact.id)
  );

  const contactsToAddListing = selectedContacts.filter(
    (newContact) => !currentListingFavoriteContacts.some((contact) => contact.id === newContact.id)
  );

  const contactsRefToDeleteListing: firebase.firestore.DocumentReference[] = [];

  for (const contact of contactsToDeleteListing) {
    const contactSnapshot = await getFavoritesListingsQuery(userEmail, contact.id).get();

    if (contactSnapshot.empty) continue;

    const contactsRef = contactSnapshot.docs.map(({ ref }) => ref);

    contactsRefToDeleteListing.push(...contactsRef);
  }

  const deleteListingsFromFavoriteContactsActions = [];

  for (const contactRef of contactsRefToDeleteListing) {
    const deleteListingFromContactAction = contactRef.update({
      listingIds: firebase.firestore.FieldValue.arrayRemove(listingId)
    });

    deleteListingsFromFavoriteContactsActions.push(deleteListingFromContactAction);
  }

  const addContactsToFavoriteActions = [];

  for (const contact of contactsToAddListing) {
    const addContactsToListingAction = createFavoriteIfNotExistsAndAddListingToFavorites(
      userEmail,
      contact.id,
      listingId
    );

    addContactsToFavoriteActions.push(addContactsToListingAction);
  }

  await Promise.all([
    ...deleteListingsFromFavoriteContactsActions,
    ...addContactsToFavoriteActions
  ]);

  return {
    contactsToAddListing,
    contactsToDeleteListing
  };
}
